/**
 * Выбор покупки, переживающий вход (aisearch.ru).
 *
 * Человек выбрал тариф на /pricing, не войдя. Раньше кнопка вела на
 * голый `/login`, и после входа он оказывался в кабинете «вообще»:
 * выбор терялся, и найти его заново хотели не все. Воронка Метрики за
 * 30 дней: «Открыл тарифы» 148 → «Начал оплату» 16 → «Оплата прошла» 7.
 *
 * Выбор живёт в трёх местах, от быстрого к надёжному:
 *
 * 1. В адресе шага (`/dashboard/pokupka?vid=…&kod=…&t=…`). Его несёт
 *    `callbackUrl` через вход паролем, регистрацию и Яндекс ID.
 * 2. В куке на сутки: на случай, когда адрес теряется по дороге
 *    (ссылка из письма открывает `/auth/confirm` или `/auth/reset`
 *    без `callbackUrl`).
 * 3. В «намерении покупки» на сервере API, по почте: когда письмо
 *    открыли на другом устройстве, где нет ни адреса, ни куки
 *    (`lib/api/namerenie.ts`).
 *
 * Здесь только чистые правила: разбор, сборка и сроки. Без React и без
 * сети, чтобы проверять их тестами напрямую. Модуль безопасен для
 * браузера, middleware и сервера одновременно.
 *
 * Ни почты, ни имени в выборе нет: только вид, код предложения и время.
 */

/** Виды покупок. `paket_skrytyj` и `bonus` — для будущих /paket и /bonus. */
export const VIDY_POKUPKI = [
  'tarif',
  'paket_katalog',
  'paket_skrytyj',
  'bonus',
  'licenziya',
] as const;

export type VidPokupki = (typeof VIDY_POKUPKI)[number];

/** Что человек выбрал и когда. */
export interface Vybor {
  vid: VidPokupki;
  /** Идентификатор в каталоге API (`tariff_pro_v2`) или код ссылки. */
  kod: string;
  /** Когда выбран, секунды эпохи. */
  t: number;
}

/** Шаг «Продолжить покупку». В кабинете: туда пускают только вошедших. */
export const PUT_SHAGA = '/dashboard/pokupka';

/** Кука с выбором. Только идентификатор выбора, ничего личного. */
export const KUKA_VYBORA = 'ais_vybor';

/**
 * Кука «плашку закрыли»: на сутки, если API не успел забыть намерение.
 * Новый выбор её снимает (middleware): «нет» относилось к прежнему.
 */
export const KUKA_SKRYTO = 'ais_vybor_skryt';

/** Сколько живёт выбор: сутки, как и кука. */
export const SROK_VYBORA_S = 24 * 60 * 60;

/**
 * Код предложения: латиница, цифры, `_` и `-`.
 *
 * Код уходит в адрес запроса к API частью пути и в адрес шага, поэтому
 * ни слэша, ни двоеточия, ни точки в нём быть не может: так выбор не
 * превратится ни в чужой путь API, ни в ссылку наружу.
 */
const KOD = /^[A-Za-z0-9_-]{1,64}$/;

/** Запас на расхождение часов: выбор «из будущего» на пять минут ещё свой. */
const ZAPAS_S = 5 * 60;

export function jeVid(v: unknown): v is VidPokupki {
  return typeof v === 'string' && (VIDY_POKUPKI as readonly string[]).includes(v);
}

export function jeKod(k: unknown): k is string {
  return typeof k === 'string' && KOD.test(k);
}

/** Секунды эпохи сейчас. Отдельно ради тестов. */
export function sejchasS(now: number = Date.now()): number {
  return Math.floor(now / 1000);
}

/**
 * Выбор из параметров адреса.
 *
 * Без `t` — выбор сделан только что: так приходит ссылка с витрины,
 * собранной заранее (время в неё не вписать). Метку ставит middleware,
 * когда отправляет гостя на вход.
 */
export function vyborIzParametrov(
  get: (name: string) => string | null | undefined,
  now: number = Date.now(),
): Vybor | null {
  const vid = get('vid');
  const kod = get('kod');
  if (!jeVid(vid) || !jeKod(kod)) return null;
  const syroeT = get('t');
  if (syroeT === null || syroeT === undefined || syroeT === '') {
    return { vid, kod, t: sejchasS(now) };
  }
  if (!/^\d{1,12}$/.test(syroeT)) return null;
  return { vid, kod, t: Number(syroeT) };
}

/** Адрес шага с выбором. Всегда свой путь от корня. */
export function adresShaga(v: Vybor, dop?: Record<string, string>): string {
  const q = new URLSearchParams({ vid: v.vid, kod: v.kod, t: String(v.t) });
  for (const [k, z] of Object.entries(dop ?? {})) q.set(k, z);
  return `${PUT_SHAGA}?${q.toString()}`;
}

/**
 * Выбор из адреса возврата (`callbackUrl`).
 *
 * Только если это сам шаг: любой другой путь выбором не считается, и
 * чужой адрес отсюда не достать никак.
 */
export function vyborIzAdresa(
  adres: string | null | undefined,
  now: number = Date.now(),
): Vybor | null {
  if (!adres?.startsWith(`${PUT_SHAGA}?`)) return null;
  const q = new URLSearchParams(adres.slice(PUT_SHAGA.length + 1));
  return vyborIzParametrov((k) => q.get(k), now);
}

/** Значение куки: `vid.kod.t`. Точки в коде быть не может (см. KOD). */
export function znachenieKuki(v: Vybor): string {
  return `${v.vid}.${v.kod}.${String(v.t)}`;
}

export function vyborIzKuki(znachenie: string | null | undefined): Vybor | null {
  if (!znachenie) return null;
  const chasti = znachenie.split('.');
  if (chasti.length !== 3) return null;
  const [vid, kod, t] = chasti;
  if (!jeVid(vid) || !jeKod(kod) || !t || !/^\d{1,12}$/.test(t)) return null;
  return { vid, kod, t: Number(t) };
}

/** Кука из строки `document.cookie` (для страниц без сервера). */
export function vyborIzStrokiKuk(stroka: string): Vybor | null {
  for (const chast of stroka.split(';')) {
    const [imya, ...ostalnoe] = chast.trim().split('=');
    if (imya === KUKA_VYBORA) {
      try {
        return vyborIzKuki(decodeURIComponent(ostalnoe.join('=')));
      } catch {
        return null;
      }
    }
  }
  return null;
}

/** Выбор старше суток (или подозрительно из будущего) — устарел. */
export function ustarel(v: Vybor, now: number = Date.now()): boolean {
  const s = sejchasS(now);
  return s - v.t > SROK_VYBORA_S || v.t - s > ZAPAS_S;
}

/** Один и тот же выбор (время не в счёт). */
export function totZhe(a: Vybor | null, b: Vybor | null): boolean {
  return Boolean(a && b && a.vid === b.vid && a.kod === b.kod);
}

/** Параметры куки: SameSite=Lax, сутки, весь сайт. */
export function parametryKuki(secure: boolean) {
  return {
    path: '/',
    maxAge: SROK_VYBORA_S,
    sameSite: 'lax' as const,
    secure,
    // Не httpOnly: страницы подтверждения почты и сброса пароля читают
    // её в браузере, чтобы после входа вести сразу на шаг. Внутри только
    // вид и код предложения, ничего, что стоило бы прятать от скриптов.
    httpOnly: false,
  };
}

/** Куда вести после входа со страниц по ссылке из письма. */
export function vozvratIzKuki(stroka: string, now: number = Date.now()): string {
  const v = vyborIzStrokiKuk(stroka);
  if (!v || ustarel(v, now)) return '/dashboard';
  return adresShaga(v, { otkuda: 'vhod' });
}

/** Почта похожа на почту: только её и шлём в намерение покупки. */
const POCHTA = /^[^\s@]{1,64}@[^\s@]{1,255}$/;

export function pohozhaNaPochtu(s: unknown): s is string {
  return typeof s === 'string' && s.length <= 320 && POCHTA.test(s.trim());
}

/** Есть ли что отправлять в намерение: выбор в адресе возврата или кука. */
export function estVybor(callbackUrl: string, strokaKuk: string): boolean {
  return vyborIzAdresa(callbackUrl) !== null || vyborIzStrokiKuk(strokaKuk) !== null;
}
